import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { Subscription, first, map, merge, switchMap } from 'rxjs';
import { Site } from '@reenstudio/app/core/models/site/site';
import { SiteApiService } from '@reenstudio/app/core/services/api/site.services';
import { MaterialModule } from '@reenstudio/app/material.module';
import { AbstractListComponent } from '@reenstudio/app/shared/components/abstract-list/abstract-list.component';
import { AlertDialogService } from '@reenstudio/app/shared/services/alert-dialog/alert-dialog.service';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { StatusLabelComponent } from '@reenstudio/app/shared/components/status-label/status-label.component';
import { PageEvent } from '@angular/material/paginator';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NoDataComponent } from '@reenstudio/app/shared/components/no-data/no-data.component';
import { NGXLogger } from 'ngx-logger';
import { SiteService } from '@reenstudio/app/core/services/site-service';
import { CerService } from '@reenstudio/app/core/services/cer-service';
import { Cer } from '@reenstudio/app/core/models/cer/cer';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { EventService } from '@reenstudio/app/core/services/event-service';

@Component({
  selector: 'app-site-list',
  templateUrl: './site-list.component.html',
  styleUrls: ['./site-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    RouterModule,
    StatusLabelComponent,
    NgxSkeletonLoaderModule,
    NoDataComponent,
  ],
})
export class SiteListComponent extends AbstractListComponent {
  @Input() override displayedColumns = [
    'type',
    'name',
    'city',
    'address',
    'cer',
    'status',
    'actions',
  ];

  override dataTable: MatTableDataSource<Site>;
  subscription: Subscription;

  statuses: any = {
    active: {
      text: 'Abilitato',
      color: 'success',
    },
    unactive: {
      text: 'Disattivo',
      color: 'error',
    },
  };

  pageSize: number;
  pageIndex: number;
  pageTotal: number;

  constructor(
    private siteService: SiteService,
    private siteApiService: SiteApiService,
    private cerService: CerService,
    private alertService: AlertDialogService,
    private snackBarService: SnackbarService,
    private logger: NGXLogger,
    private storageService: StorageService
  ) {
    super();
    this.dataTable = new MatTableDataSource<Site>([]);
  }

  protected override getList() {
    merge(this.filterChanged, this.sort.sortChange)
      .pipe(
        switchMap(() => {
          this.loading.next(true);
          const params = {
            siteId: this.filters?.cerId,
            siteName: this.filters?.cerName,
            enabled: true,
            page: this.pageIndex + 1,
            pageSize: this.pageSize,
          };

          return this.siteApiService.getSites(params);
        }),
        map((res) => {
          this.logger.debug('Sites received data: ', res);
          this.dataLength = res.pagination.total;
          res.data.forEach((element) => {
            if (element.cer_id) {
              const cer: Cer | null = this.cerService.getStorageCer(
                element.cer_id
              );
              if (cer) {
                element.cerDetails = cer;
              }
            }
          });
          return res.data;
        })
      )
      .subscribe({
        next: (data) => {
          this.dataTable.data = data;
          this.loading.next(false);
        },
      });
  }

  applyFilter(event: Event) {
    if (event) {
    }
  }

  handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.triggerFilterChange();
  }

  delete(siteId: number) {
    const site = this.siteService.getStorageSite(siteId);
    if (site) {
      this.alertService.create(
        {
          title: 'Elimina sito',
          message: `Stai eliminando il sito ${site.description}`,
          confirm: 'Conferma',
          cancel: 'Annulla',
          type: 'warning',
        },
        () => {
          this.loading.next(true);
          this.siteApiService
            .deleteSite(siteId)
            .pipe(
              switchMap(() => {
                const params = { enabled: true };
                return this.siteApiService.getSites(params);
              })
            )
            .subscribe({
              next: (data) => {
                this.storageService.setSites(data.data);

                this.snackBarService.success('Sito eliminato con successo');
                this.getList();
                EventService.changeCer.emit();
              },
              complete: () => this.loading.next(false),
            });
        }
      );
    }
  }
}
